import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { Activity, Users, Bookmark, Trash2, CalendarClock, AlertTriangle, Loader2 } from 'lucide-react';
import { EVENT_TYPES, EVENT_TYPE_STYLES } from '../../data/calendarEventTypes';
import WhoLabel from '../common/WhoLabel';
import ModalShell, { MODAL_CANCEL_CLASS, MODAL_SUBMIT_CLASS, MODAL_DANGER_CLASS } from '../common/ModalShell';
import TimeSelect from '../common/TimeSelect';
import { formatTimeRange } from '../../utils/clock';

// The form behind a doctor's calendar event (surgery / meeting / other).
//
// Presentational on purpose: it owns the fields and their validation, nothing
// else. Saving, deleting, toasting and refetching the visible range stay on
// Appointment.jsx, which is where every other write on that page already lives.
//
// TIME ZONE. Every field here is read as a wall-clock time in ONE zone — the
// doctor's, falling back to the browser's — and the page converts it to the
// startUTC/endUTC the API stores. The zone is printed under the fields rather
// than assumed, because an admin in Delhi editing a doctor in Paris otherwise
// has no way to know which 09:00 they typed.
//
// A BLOCK IS ALWAYS A FROM AND A TO. The all-day switch is gone (owner,
// 2026-09-08): it put a card in a lane above the grid where it could sit on
// top of appointments nobody had moved, and a whole day off is what leave is
// for. A block that really lasts the day is typed 00:00–23:59 like any other.
//
// ONE THING PER TIME. As soon as the date and both times are valid, the form
// asks the page (`onCheckConflicts`) what a block there would hit — live
// appointments and other events — and lists them under the time fields with
// Save greyed out until the time is changed. The API refuses a clash with
// 409 whatever this form shows; the check here is so the clash is visible
// BEFORE the user has typed a title and notes and pressed Save. If the check
// itself cannot run (network), Save stays enabled and the server has the
// last word — a broken lookup must not lock the form.

const TYPE_ICONS = { surgery: Activity, meeting: Users, other: Bookmark };
const CONFLICT_CHECK_DEBOUNCE_MS = 350;

const CalendarEventModal = ({ draft, timeZone, saving, onClose, onSave, onDelete, onCheckConflicts, recheckToken = 0 }) => {
    const { t } = useTranslation('appointment');

    const [eventType, setEventType] = useState('surgery');
    const [title, setTitle] = useState('');
    const [notes, setNotes] = useState('');
    const [date, setDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [formError, setFormError] = useState('');

    // What the picked time collides with. `null` = not asked (or the time is
    // not valid yet); an object = the server's answer for the CURRENT fields.
    const [conflicts, setConflicts] = useState(null);
    const [checking, setChecking] = useState(false);
    const [checkFailed, setCheckFailed] = useState(false);
    // Each check gets a ticket; a slow answer to an older question is dropped
    // so the panel never shows the clash of a time the user has moved off.
    const checkTicket = useRef(0);

    // Re-seed whenever a different slot or event is opened. The modal is
    // mounted only while `draft` exists, but the page reuses it for the next
    // click without unmounting in between.
    useEffect(() => {
        if (!draft) return;
        setEventType(draft.eventType || 'surgery');
        setTitle(draft.title || '');
        setNotes(draft.notes || '');
        setDate(draft.date || '');
        setStartTime(draft.startTime || '');
        setEndTime(draft.endTime || '');
        setFormError('');
        setConflicts(null);
        setCheckFailed(false);
    }, [draft]);

    const timeValid = !!date && !!startTime && !!endTime && endTime > startTime;
    // On an edit whose time was left exactly as seeded, the page saves the
    // stored instants back verbatim and the server skips its clash check
    // (the block is not moving). Mirror that here: no check, no panel — a row
    // from before the one-thing-per-time rule can still have its title fixed.
    const timeUntouched = !!draft?.eventId
        && date === (draft.date || '')
        && startTime === (draft.startTime || '')
        && endTime === (draft.endTime || '');

    // Ask what the time hits, debounced, whenever the time changes — and again
    // when the page bumps `recheckToken` after the server refused a save, so
    // the list on screen is the one the server just answered with.
    useEffect(() => {
        if (!draft || typeof onCheckConflicts !== 'function') return undefined;
        if (!timeValid || timeUntouched) {
            // Retire any answer still in flight for the time just moved off,
            // or it would paint a clash under fields that no longer hold it.
            checkTicket.current += 1;
            setConflicts(null);
            setChecking(false);
            setCheckFailed(false);
            return undefined;
        }
        const ticket = ++checkTicket.current;
        setChecking(true);
        setCheckFailed(false);
        const timer = setTimeout(async () => {
            try {
                const result = await onCheckConflicts({ date, startTime, endTime, eventId: draft.eventId || null });
                if (ticket !== checkTicket.current) return;
                setConflicts({
                    appointments: Array.isArray(result?.appointments) ? result.appointments : [],
                    events: Array.isArray(result?.events) ? result.events : [],
                });
            } catch {
                if (ticket !== checkTicket.current) return;
                setConflicts(null);
                setCheckFailed(true);
            } finally {
                if (ticket === checkTicket.current) setChecking(false);
            }
        }, CONFLICT_CHECK_DEBOUNCE_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft, date, startTime, endTime, timeValid, recheckToken]);

    if (!draft) return null;

    const isEdit = !!draft.eventId;
    const hasConflict = !!conflicts && (conflicts.appointments.length > 0 || conflicts.events.length > 0);
    const saveBlocked = saving || checking || hasConflict;

    // The clash's own times, shown in the zone the form is typed in. The date
    // is added only when it differs from the picked one, which happens for a
    // block that runs past midnight.
    const fmtRange = (startIso, endIso) => {
        const s = DateTime.fromISO(startIso, { zone: 'utc' }).setZone(timeZone);
        const e = DateTime.fromISO(endIso, { zone: 'utc' }).setZone(timeZone);
        const sameDay = s.toISODate() === date;
        const dayPrefix = sameDay ? '' : `${s.toFormat('dd LLL')} · `;
        return `${dayPrefix}${formatTimeRange(s.toJSDate(), e.toJSDate(), { timeZone })}`;
    };

    const submit = () => {
        const cleanTitle = title.trim();
        if (!cleanTitle) {
            setFormError(t('appointment.event_title_required', 'Give the event a title.'));
            return;
        }
        if (!date) {
            setFormError(t('appointment.event_date_required', 'Pick a date for the event.'));
            return;
        }
        if (!startTime || !endTime) {
            setFormError(t('appointment.event_time_required', 'Pick a start and an end time.'));
            return;
        }
        if (endTime <= startTime) {
            setFormError(t('appointment.event_time_invalid', 'The end time must be after the start time.'));
            return;
        }
        if (hasConflict) {
            setFormError(t('appointment.event_conflict_title', 'This time is already taken'));
            return;
        }
        setFormError('');
        onSave({
            eventId: draft.eventId || null,
            eventType,
            title: cleanTitle,
            notes: notes.trim(),
            date,
            startTime,
            endTime,
        });
    };

    return (
        <ModalShell
            title={(
                <span data-testid="calendar-event-modal-title">
                    {isEdit
                        ? t('appointment.event_edit_title', 'Edit calendar event')
                        : t('appointment.event_new_title', 'New calendar event')}
                </span>
            )}
            onClose={onClose}
            size="md"
            testId="calendar-event-modal"
            closeTestId="calendar-event-modal-close"
            footer={(
                <>
                    {isEdit && (
                        <button
                            type="button"
                            onClick={onDelete}
                            disabled={saving}
                            data-testid="calendar-event-delete"
                            className={`${MODAL_DANGER_CLASS} mr-auto`}
                        >
                            <Trash2 className="w-4 h-4" />
                            {t('appointment.event_delete', 'Delete')}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        className={MODAL_CANCEL_CLASS}
                    >
                        {t('appointment.cancel_button', 'Cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={saveBlocked}
                        data-testid="calendar-event-save"
                        title={hasConflict ? t('appointment.event_conflict_title', 'This time is already taken') : undefined}
                        className={MODAL_SUBMIT_CLASS}
                    >
                        {saving
                            ? t('appointment.event_saving', 'Saving…')
                            : t('appointment.event_save', 'Save event')}
                    </button>
                </>
            )}
        >
                {/* ---- body ---- */}
                <div className="space-y-4">
                    {/* Type */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                            {t('appointment.event_type_label', 'Type')}
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                            {EVENT_TYPES.map((type) => {
                                const Icon = TYPE_ICONS[type];
                                const style = EVENT_TYPE_STYLES[type];
                                const on = type === eventType;
                                return (
                                    <button
                                        key={type}
                                        type="button"
                                        onClick={() => setEventType(type)}
                                        data-testid={`calendar-event-type-${type}`}
                                        className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-2.5 transition-all cursor-pointer ${on ? 'ring-1' : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                        style={on ? { borderColor: style.bg, boxShadow: `0 0 0 1px ${style.bg}`, backgroundColor: `${style.bg}1A` } : undefined}
                                    >
                                        <Icon className="w-4 h-4" style={{ color: on ? style.bg : '#6b7280' }} />
                                        <span className={`text-xs font-semibold ${on ? 'text-gray-800' : 'text-gray-600'}`}>
                                            {t(`appointment.event_type_${type}`, type)}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Title */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">
                            {t('appointment.event_title_label', 'Title')}
                        </label>
                        <input
                            type="text"
                            value={title}
                            maxLength={120}
                            onChange={(e) => setTitle(e.target.value)}
                            data-testid="calendar-event-title-input"
                            placeholder={t('appointment.event_title_placeholder', 'e.g. Theatre 2 — knee replacement')}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                        />
                    </div>

                    {/* Date */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">
                            {t('appointment.event_date_label', 'Date')}
                        </label>
                        <input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            data-testid="calendar-event-date-input"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                        />
                    </div>

                    {/* Start / end — hour, minute, AM/PM (the one clock format);
                        the value is still "HH:mm". Wraps when the two do not fit. */}
                    <div className="flex flex-wrap gap-x-6 gap-y-3">
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">
                                {t('appointment.event_start_label', 'Start')}
                            </label>
                            <TimeSelect
                                value={startTime}
                                onChange={setStartTime}
                                testId="calendar-event-start-input"
                                ariaLabel={t('appointment.event_start_label', 'Start')}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">
                                {t('appointment.event_end_label', 'End')}
                            </label>
                            <TimeSelect
                                value={endTime}
                                onChange={setEndTime}
                                testId="calendar-event-end-input"
                                ariaLabel={t('appointment.event_end_label', 'End')}
                            />
                        </div>
                    </div>

                    {/* What this time would collide with */}
                    {checking && (
                        <p data-testid="calendar-event-conflict-checking" className="flex items-center gap-1.5 text-[11px] text-slate-500">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            {t('appointment.event_conflict_checking', 'Checking this time…')}
                        </p>
                    )}
                    {!checking && hasConflict && (
                        <div
                            data-testid="calendar-event-conflicts"
                            className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 space-y-1.5"
                        >
                            <p className="flex items-center gap-1.5 text-sm font-semibold text-red-700">
                                <AlertTriangle className="w-4 h-4" />
                                {t('appointment.event_conflict_title', 'This time is already taken')}
                            </p>
                            <ul className="space-y-1">
                                {conflicts.appointments.map((a) => (
                                    <li
                                        key={`appt-${a.appointmentId}`}
                                        data-testid={`calendar-event-conflict-appt-${a.appointmentId}`}
                                        className="flex items-center gap-2 text-xs text-red-800"
                                    >
                                        <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#4ABDBD' }} />
                                        <span className="font-semibold">{t('appointment.event_conflict_appointment', 'Appointment')}</span>
                                        {a.patientName && <span className="truncate">· <WhoLabel role="patient" name={a.patientName} nameClassName="font-semibold text-red-800" testId={`calendar-event-conflict-patient-${a.appointmentId}`} /></span>}
                                        <span className="ml-auto whitespace-nowrap tabular-nums">{fmtRange(a.start, a.end)}</span>
                                    </li>
                                ))}
                                {conflicts.events.map((ev) => {
                                    const style = EVENT_TYPE_STYLES[ev.eventType] || EVENT_TYPE_STYLES.other;
                                    return (
                                        <li
                                            key={`ev-${ev.eventId}`}
                                            data-testid={`calendar-event-conflict-event-${ev.eventId}`}
                                            className="flex items-center gap-2 text-xs text-red-800"
                                        >
                                            <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: style.bg }} />
                                            <span className="font-semibold">{t(`appointment.event_type_${ev.eventType || 'other'}`, ev.eventType || 'other')}</span>
                                            {ev.title && <span className="truncate">· {ev.title}</span>}
                                            <span className="ml-auto whitespace-nowrap tabular-nums">{fmtRange(ev.startUTC, ev.endUTC)}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                            <p className="text-[11px] text-red-700/80">
                                {t('appointment.event_conflict_hint', 'Move or cancel what is there first, or pick another time.')}
                            </p>
                        </div>
                    )}
                    {!checking && !hasConflict && checkFailed && (
                        <p data-testid="calendar-event-conflict-check-failed" className="flex items-center gap-1.5 text-[11px] text-slate-500">
                            <CalendarClock className="w-3.5 h-3.5" />
                            {t('appointment.event_conflict_check_failed', 'Could not check this time for clashes. The server will still refuse one.')}
                        </p>
                    )}

                    {/* Notes */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">
                            {t('appointment.event_notes_label', 'Notes')}
                        </label>
                        <textarea
                            rows={3}
                            value={notes}
                            maxLength={1000}
                            onChange={(e) => setNotes(e.target.value)}
                            data-testid="calendar-event-notes-input"
                            placeholder={t('appointment.event_notes_placeholder', 'Anything the team should know (optional)')}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] resize-none"
                        />
                    </div>

                    <p className="text-[11px] text-slate-400" data-testid="calendar-event-timezone-note">
                        {t('appointment.event_timezone_note', 'Times are in {{zone}}.', { zone: timeZone })}
                    </p>
                    <p className="text-xs text-[#8a6a35] bg-[#D7A05C]/10 border border-[#D7A05C]/30 rounded-xl px-3 py-2">
                        {t('appointment.event_blocks_slots_note', 'Patients cannot book any slot this event covers.')}
                    </p>

                    {formError && (
                        <p data-testid="calendar-event-form-error" className="text-xs text-red-600">{formError}</p>
                    )}
                </div>

        </ModalShell>
    );
};

export default CalendarEventModal;
